import { useEffect, useMemo, useState } from "react";
import {
  Loader2, RefreshCw, AlertTriangle, ChevronDown, ChevronUp, ChevronLeft,
  ChevronRight, Users, Clock, Sun, Moon, Calendar as CalendarIcon,
  Layers, ClipboardList, Wrench, PenTool, ShieldAlert, Boxes, Download,
  Check, X as XIcon, Sparkles, Repeat, MessageSquare, FileCheck2,
  ClipboardCheck,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { todayLocalIso } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useSiteShift } from "@/hooks/useSiteShift";
import { useSearchParams } from "react-router-dom";
import { downloadReportPdf } from "@/lib/reportPdf";

/**
 * ShiftSummary — 1:1 clone of the mobile app's Shift Summary screen.
 *  - Pulls /api/shift-summary for headline counts & timeline.
 *  - Pulls /api/get-wear for the Teeth % Worn table (Worst per asset).
 *  - Row shows Asset · Worst Tooth · Worst Lip Shroud · Last Inspected + WHO/WHEN.
 */
export default function ShiftSummary() {
  const { user } = useAuth();
  const { shift: liveShift } = useSiteShift();
  const [params, setParams] = useSearchParams();
  // Date + shift come from URL so links are shareable. Default to today's live
  // shift so the page opens where the crew currently is.
  const todayIso = todayLocalIso();
  const urlDate = params.get("date") || todayIso;
  const urlShift = (params.get("shift") || liveShift || "day").toLowerCase();
  const selectedShift = urlShift;
  const selectedDate = useMemo(() => new Date(`${urlDate}T00:00:00`), [urlDate]);
  const weekNo = weekOfYear(selectedDate);
  const isToday = urlDate === todayIso;

  const setDateShift = (dateStr, shift) => {
    const next = new URLSearchParams(params);
    next.set("date", dateStr);
    next.set("shift", shift);
    setParams(next, { replace: false });
  };
  const stepDay = (delta) => {
    const d = new Date(selectedDate); d.setDate(d.getDate() + delta);
    setDateShift(todayLocalIso(d), selectedShift);
  };
  const toggleShift = () => setDateShift(urlDate, selectedShift === "day" ? "night" : "day");

  const [summary, setSummary] = useState(null);
  const [wear, setWear] = useState(null);
  const [handovers, setHandovers] = useState([]);
  const [breakdowns, setBreakdowns] = useState([]);
  const [customJobs, setCustomJobs] = useState([]);
  const [openJobs, setOpenJobs] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [ppfs, setPpfs] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(true);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  // Default: Asset alphabetical (A→Z). Josh Feb 23 — much easier to scan
  // the wear table when it's in alphabetical order, especially with 80+
  // machines across a site.
  const [sortKey, setSortKey] = useState("asset");
  const [sortDir, setSortDir] = useState("asc");
  const [expanded, setExpanded] = useState(null); // asset_id currently being edited
  const [rowBusy, setRowBusy] = useState({});      // asset_id -> boolean

  const reload = async () => {
    setBusy(true); setErr("");
    // Pass date + shift to /shift-summary so the backend returns the correct
    // stats/timeline for the historical window we're viewing.
    const ssPath = `/shift-summary?date=${encodeURIComponent(urlDate)}&shift=${encodeURIComponent(selectedShift)}`;
    const [sumRes, wearRes, hoRes, bdRes, iRes, pRes, adRes] = await Promise.allSettled([
      api.get(ssPath),
      api.get("/get-wear"),
      api.get("/handovers"),
      api.get("/breakdowns"),
      api.get("/inspections"),
      api.get("/ppfs"),
      api.get("/asset-demands"),
    ]);
    if (sumRes.status === "fulfilled") setSummary(sumRes.value.data);
    else if (wearRes.status !== "fulfilled") setErr(apiError(sumRes.reason));

    const listOf = (res) => {
      if (res.status !== "fulfilled") return [];
      const d = res.value.data;
      return Array.isArray(d) ? d : (d?.items || d?.results || d?.data || []);
    };
    setWear(listOf(wearRes));
    setHandovers(listOf(hoRes));
    // Custom jobs (completed asset-demands) live inside the /shift-summary
    // payload — the mobile app reads them from `summary.custom_jobs`.
    const sumData = sumRes.status === "fulfilled" ? sumRes.value.data : null;
    setCustomJobs(Array.isArray(sumData?.custom_jobs) ? sumData.custom_jobs : []);
    setBreakdowns(listOf(bdRes));
    setInspections(listOf(iRes));
    setPpfs(listOf(pRes));
    // OPEN custom jobs (asset-demands with status != done). Josh (Feb 26):
    // "shift summer not showing all jobs down like custom jobs and that" —
    // completed jobs live in `summary.custom_jobs`, still-open ones live
    // here so the crew doesn't lose track of what's outstanding.
    const adArr = listOf(adRes);
    const stillOpen = adArr.filter((d) => {
      const s = String(d.status || "").toLowerCase();
      return s !== "done" && s !== "closed" && s !== "cancelled";
    });
    setOpenJobs(stillOpen);
    setBusy(false);
  };
  useEffect(() => { reload(); }, [urlDate, selectedShift]);

  // A record is in the selected shift window when its timestamp falls within
  // that shift's hours on that date. Day: 06:00–18:00 local; Night crosses
  // midnight (18:00 same day → 06:00 next day).
  const inShiftWindow = (ts) => {
    if (!ts) return false;
    const d = new Date(ts);
    if (isNaN(d.getTime())) return false;
    const [Y, M, D] = urlDate.split("-").map(Number);
    if (selectedShift === "day") {
      const start = new Date(Y, M - 1, D, 6, 0, 0);
      const end = new Date(Y, M - 1, D, 18, 0, 0);
      return d >= start && d < end;
    }
    // Night: 18:00 same day → 06:00 next day
    const start = new Date(Y, M - 1, D, 18, 0, 0);
    const end = new Date(Y, M - 1, D + 1, 6, 0, 0);
    return d >= start && d < end;
  };

  const currentSwingHandovers = useMemo(() => {
    return (handovers || []).filter((h) => {
      const ts = h.date || h.created_at || h.ts;
      const site = user?.site_code;
      const siteOk = !site || !h.site_code || String(h.site_code) === String(site);
      // Only count handovers that have been finalised — either a signature
      // is attached, or there's real content (work_completed / machine_status).
      // A draft with an empty body doesn't belong in a shift summary.
      const signed = !!(h.sign_off_signature || h.sign_off_name);
      const hasContent = (h.work_completed && String(h.work_completed).trim()) || (h.machine_status && String(h.machine_status).trim());
      return inShiftWindow(ts) && siteOk && (signed || hasContent);
    }).sort((a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0));

    // inShiftWindow reads urlDate + selectedShift which are already deps
  }, [handovers, user, urlDate, selectedShift]);

  const currentSwingBreakdowns = useMemo(() => {
    return (breakdowns || []).filter((b) => {
      const ts = b.breakdown_time || b.created_at || b.ts;
      const site = user?.site_code;
      const siteOk = !site || !b.site_code || String(b.site_code) === String(site);
      // Exclude drafts — a breakdown is only "reported" once the tradesman
      // has finalised it. Awaiting-supervisor-approval breakdowns still count.
      const finalised = b.is_finalised === true || !!b.approved_at || /approved|complete|signed|finalised/i.test(String(b.status || ""));
      return inShiftWindow(ts) && siteOk && finalised;
    }).sort((a, b) => new Date(b.breakdown_time || b.created_at || 0) - new Date(a.breakdown_time || a.created_at || 0));
  }, [breakdowns, user, urlDate, selectedShift]);

  // Completed inspections in this shift window — used in the WORK DONE section
  // so supervisors can see which inspections were signed off during the shift.
  const completedInspections = useMemo(() => {
    return (inspections || []).filter((i) => {
      const ts = i.approved_at || i.ready_at || i.updated_at;
      const status = String(i.status || "").toLowerCase();
      const isDone = status.includes("approved") || status.includes("signed") || status.includes("complete");
      const site = user?.site_code;
      const siteOk = !site || !i.site_code || String(i.site_code) === String(site);
      return isDone && inShiftWindow(ts) && siteOk;
    }).sort((a, b) => new Date(b.approved_at || b.updated_at || 0) - new Date(a.approved_at || a.updated_at || 0));
  }, [inspections, user, urlDate, selectedShift]);

  // Completed PPFs in this shift window
  const completedPpfs = useMemo(() => {
    return (ppfs || []).filter((p) => {
      const ts = p.completed_at || p.approved_at || p.updated_at;
      const status = String(p.status || "").toLowerCase();
      const isDone = status.includes("approved") || status.includes("complete") || status.includes("closed");
      const site = user?.site_code;
      const siteOk = !site || !p.site_code || String(p.site_code) === String(site);
      return isDone && inShiftWindow(ts) && siteOk;
    }).sort((a, b) => new Date(b.completed_at || b.updated_at || 0) - new Date(a.completed_at || a.updated_at || 0));
  }, [ppfs, user, urlDate, selectedShift]);

  // Shared helpers for both adjustWear + markNew: convert a numeric worst to
  // the string token the API stores (0 → "new") and build a per-position dict
  // PATCH shape ({tooth:{"Tooth Pos 1":"50", …}}) which is what /get-wear/{id}
  // actually accepts (numeric worst gets 422 rejected).
  const wearToken = (n) => (Number(n) <= 0 ? "new" : String(n));
  const catFromLabel = (label) => {
    const L = String(label || "").toLowerCase();
    if (L.includes("wing")) return "wing_shroud";
    if (L.includes("lip")) return "lip_shroud";
    if (L.includes("tooth")) return "tooth";
    return null;
  };
  const buildDictPatch = (assetId, patch) => {
    // Load current /get-wear entry so we know which position labels exist.
    const cur = (wear || []).find((w) => String(w.asset_id) === String(assetId)) || {};
    const body = {};
    ["tooth", "lip_shroud", "wing_shroud"].forEach((cat) => {
      if (patch[cat] == null) return;
      const keys = Object.keys(cur[cat] || {});
      const tok = wearToken(patch[cat]);
      body[cat] = keys.length > 0
        ? Object.fromEntries(keys.map((k) => [k, tok]))
        : { [cat === "tooth" ? "ALL TEETH" : cat === "lip_shroud" ? "ALL LIP SHROUDS" : "ALL WING SHROUDS"]: tok };
    });
    return body;
  };
  // When wear is adjusted from the Shift Summary, also update the LATEST
  // inspection's wear_pct items for that asset — so the inspection view
  // (and the mobile app reading it) sees the same values without waiting
  // for a fresh inspection to be run.
  const propagateWearToLatestInspection = async (assetId, patch) => {
    const latest = (inspections || [])
      .filter((i) => String(i.asset_id) === String(assetId))
      .sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0))[0];
    if (!latest) return;
    try {
      const { data: full } = await api.get(`/inspections/${latest.id}`);
      const wearItems = [];
      (full.sections || []).forEach((s) => (s.items || []).forEach((it) => {
        if (it.kind === "wear_pct") wearItems.push(it);
      }));
      const now = new Date().toISOString();
      const who = user?.username || user?.name || "supervisor";
      // Filter down to items whose category is actually in the patch, then
      // PATCH sequentially — prod's per-inspection write endpoint has a race
      // condition on concurrent writes (verified live: 6 parallel PATCHes →
      // only 2 persist). Sequential awaits are ~200 ms slower per item but
      // 100 % reliable, which is what the "portal → app" sync depends on.
      const targets = wearItems.filter((it) => {
        const cat = catFromLabel(it.label);
        return cat && patch[cat] != null;
      });
      for (const it of targets) {
        const cat = catFromLabel(it.label);
        try {
          await api.patch(`/inspections/${latest.id}/items/${it.id}`, {
            status: wearToken(patch[cat]),
            clicked_by: who,
            clicked_at: now,
            client_op_id: `ss-adjust-${it.id}-${Date.now()}`,
          });
        } catch { /* keep going — /get-wear PATCH already succeeded */ }
      }
    } catch { /* silent — /get-wear PATCH already succeeded */ }
  };

  const adjustWear = async (assetId, patch) => {
    setRowBusy((p) => ({ ...p, [assetId]: true }));
    try {
      const dictBody = buildDictPatch(assetId, patch);
      await api.patch(`/get-wear/${assetId}`, {
        ...dictBody,
        client_op_id: `wear-${assetId}-${Date.now()}`,
      });
      // Also update the latest inspection's wear_pct items so the "TEETH
      // PERCENTAGE WORN" table stays in lock-step with the inspection UI.
      await propagateWearToLatestInspection(assetId, patch);
      // Optimistic update
      setWear((prev) => (prev || []).map((r) => (
        String(r.asset_id || r.id) === String(assetId)
          ? {
              ...r,
              worst_tooth: patch.tooth ?? r.worst_tooth ?? r.tooth,
              worst_lip_shroud: patch.lip_shroud ?? r.worst_lip_shroud ?? r.lip_shroud,
              worst_wing_shroud: patch.wing_shroud ?? r.worst_wing_shroud ?? r.wing_shroud,
              last_inspected: new Date().toISOString(),
              last_inspector: user?.name || user?.username,
            }
          : r
      )));
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setRowBusy((p) => ({ ...p, [assetId]: false }));
    }
  };

  const markNew = async (assetId, category) => {
    setRowBusy((p) => ({ ...p, [assetId]: true }));
    try {
      await api.post(`/get-wear/${assetId}/mark-new`, {
        category,
        note: `Reset via portal by ${user?.name || user?.username || "supervisor"}`,
        client_op_id: `marknew-${assetId}-${category}-${Date.now()}`,
      });
      // Also flip the latest inspection's wear_pct items in this category to
      // "new" so the inspection view + mobile app reflect the change-out.
      await propagateWearToLatestInspection(assetId, { [category]: 0 });
      setWear((prev) => (prev || []).map((r) => (
        String(r.asset_id || r.id) === String(assetId)
          ? {
              ...r,
              ...(category === "tooth" ? { worst_tooth: 0 } : {}),
              ...(category === "lip_shroud" ? { worst_lip_shroud: 0 } : {}),
              ...(category === "wing_shroud" ? { worst_wing_shroud: 0 } : {}),
              last_inspected: new Date().toISOString(),
              last_inspector: user?.name || user?.username,
            }
          : r
      )));
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setRowBusy((p) => ({ ...p, [assetId]: false }));
    }
  };

  // Prefer wear from /shift-summary.excavator_teeth if present; else /get-wear.
  // IMPORTANT: prod /shift-summary omits `worst_wing_shroud` from the
  // excavator_teeth payload (verified live: only worst_tooth + worst_lip_shroud
  // returned). We MERGE the /get-wear entry per asset so the Wing Shroud
  // column stays populated — otherwise every wing cell renders as blank/NEW
  // even when the wing shrouds have real recorded wear.
  const rows = useMemo(() => {
    const src = (summary?.excavator_teeth || summary?.digger_get_wear || summary?.get_wear || wear || []);
    const wearByAsset = new Map();
    (wear || []).forEach((w) => { if (w?.asset_id) wearByAsset.set(String(w.asset_id), w); });
    return (src || []).map((r) => {
      const gw = wearByAsset.get(String(r?.asset_id));
      // Merge: prefer the FRESH /get-wear entry (which is optimistically
      // updated when the user hits a wear button) over the stale
      // `summary.excavator_teeth` snapshot that was captured at page load.
      // Fall back to the shift-summary source for fields /get-wear omits.
      const merged = gw
        ? {
            ...(r || {}),
            ...gw,
            worst_tooth: gw.worst_tooth ?? r?.worst_tooth,
            worst_lip_shroud: gw.worst_lip_shroud ?? r?.worst_lip_shroud,
            worst_wing_shroud: gw.worst_wing_shroud ?? r?.worst_wing_shroud,
            wing_shroud: gw.wing_shroud ?? r?.wing_shroud,
            lip_shroud: gw.lip_shroud ?? r?.lip_shroud,
            tooth: gw.tooth ?? r?.tooth,
          }
        : r;
      return normalise(merged);
    });
  }, [summary, wear]);

  const sorted = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (r) => {
      if (sortKey === "asset") return String(r.asset_id || r.asset_name || "").toLowerCase();
      if (sortKey === "last") return new Date(r.last_inspected || 0).getTime();
      if (sortKey === "worst_lip") return numOrNull(r.worst_lip_shroud);
      if (sortKey === "worst_wing") return numOrNull(r.worst_wing_shroud);
      return numOrNull(r.worst_tooth);
    };
    return [...rows].sort((a, b) => {
      const A = val(a), B = val(b);
      if (A === B) return 0;
      if (A === null) return 1;
      if (B === null) return -1;
      return A > B ? dir : -dir;
    });
  }, [rows, sortKey, sortDir]);

  const setSort = (k) => {
    if (k === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir(k === "asset" ? "asc" : "desc"); }
  };

  const stats = summary?.stats || summary?.counts || {};

  return (
    <div className="space-y-6" data-testid="shift-summary-page">
      {/* PDF-parity hero: Logo · SHIFT SUMMARY · meta */}
      <section
        className="panel-heavy p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 justify-between"
        data-testid="ss-hero"
      >
        <div className="flex items-center gap-5 min-w-0">
          <img
            src="/shiftlog-logo.png?v=pl2"
            alt="Pit Link"
            className="h-16 md:h-20 shrink-0"
            style={{ filter: "brightness(1.35) contrast(1.05)" }}
          />
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.4em] font-black" style={{ color: "var(--primary)" }}>
              Pit Link
            </div>
            <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight leading-none mt-1">
              SHIFT SUMMARY
            </h1>
            {/* Date navigator — prev/next day arrows + calendar dropdown + Day/Night toggle */}
            <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="ss-date-nav">
              <div className="flex items-center rounded-lg overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                <button
                  type="button"
                  onClick={() => stepDay(-1)}
                  className="px-2.5 py-1.5 hover:bg-white/10 transition"
                  data-testid="ss-prev-day"
                  title="Previous day"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setCalOpen((o) => !o)}
                  className="px-3 py-1.5 flex items-center gap-2 hover:bg-white/10 transition font-mono-tight text-xs relative"
                  data-testid="ss-open-calendar"
                  title="Pick a date"
                >
                  <CalendarIcon className="w-3.5 h-3.5" />
                  {fmtDate(urlDate)}
                </button>
                <button
                  type="button"
                  onClick={() => stepDay(1)}
                  className="px-2.5 py-1.5 hover:bg-white/10 transition disabled:opacity-40"
                  data-testid="ss-next-day"
                  title="Next day"
                  disabled={isToday}
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Day / Night toggle */}
              <div className="flex rounded-lg overflow-hidden" style={{ border: "1px solid var(--border)" }} data-testid="ss-shift-toggle">
                <button
                  type="button"
                  onClick={() => setDateShift(urlDate, "day")}
                  className="px-3 py-1.5 flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest"
                  style={selectedShift === "day"
                    ? { background: "rgba(245,130,32,0.2)", color: "var(--primary)" }
                    : { background: "transparent", color: "#a1a1aa" }}
                  data-testid="ss-shift-day"
                >
                  <Sun className="w-3.5 h-3.5" /> Day
                </button>
                <button
                  type="button"
                  onClick={() => setDateShift(urlDate, "night")}
                  className="px-3 py-1.5 flex items-center gap-1.5 text-[11px] font-black uppercase tracking-widest"
                  style={selectedShift === "night"
                    ? { background: "rgba(80,120,220,0.2)", color: "#93c5fd" }
                    : { background: "transparent", color: "#a1a1aa" }}
                  data-testid="ss-shift-night"
                >
                  <Moon className="w-3.5 h-3.5" /> Night
                </button>
              </div>

              <span className="pill !text-[10px] !py-1 !px-2.5 font-mono-tight" data-testid="ss-meta-week">
                Week {weekNo}
              </span>
              <span className="pill !text-[10px] !py-1 !px-2.5 font-mono-tight" data-testid="ss-meta-site">
                {user?.site_code || "All sites"}
              </span>
              {!isToday && (
                <button
                  type="button"
                  onClick={() => setDateShift(todayIso, liveShift || "day")}
                  className="pill !text-[10px] !py-1 !px-2.5 font-black hover:brightness-125 transition"
                  style={{ background: "rgba(245,130,32,0.15)", border: "1px solid rgba(245,130,32,0.35)", color: "var(--primary)" }}
                  data-testid="ss-jump-today"
                >
                  Jump to today
                </button>
              )}
            </div>
            {calOpen && (
              <div className="mt-2 relative" data-testid="ss-calendar-popover">
                <div className="absolute z-30 panel-heavy p-3" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                  <input
                    type="date"
                    value={urlDate}
                    max={todayIso}
                    onChange={(e) => { setDateShift(e.target.value, selectedShift); setCalOpen(false); }}
                    className="input"
                    data-testid="ss-date-input"
                    autoFocus
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            className="btn btn-sm"
            onClick={async () => {
              setPdfBusy(true);
              try {
                await downloadReportPdf({
                  date: urlDate,
                  shift: selectedShift,
                  weekNo,
                  siteCode: user?.site_code,
                  stats,
                  handovers: currentSwingHandovers,
                  breakdowns: currentSwingBreakdowns,
                  completedInspections,
                  completedPpfs,
                  customJobs,
                  hazards: summary?.hazards || summary?.active_hazards || [],
                  wearRows: sorted,
                }, "Shift Summary");
              } catch (ex) {
                setErr(apiError(ex));
              } finally { setPdfBusy(false); }
            }}
            disabled={pdfBusy || busy}
            data-testid="ss-download-pdf"
            style={{ background: "var(--surface-2)", border: "1px solid var(--border)" }}
          >
            {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download PDF
          </button>
          <button className="btn btn-sm btn-ghost" onClick={reload} disabled={busy} data-testid="ss-refresh">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Refresh
          </button>
        </div>
      </section>

      {err && (
        <div className="pill pill-red !text-sm !py-2 !px-3 justify-start">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      {/* Counts strip — defensive: only render tiles we have data for */}
      <section className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="ss-stats">
        <StatTile label="Hazards" value={pickCount(stats, ["hazards", "hazard_count"]) ?? (summary?.hazards || []).length} icon={ShieldAlert} />
        <StatTile label="Handovers" value={pickCount(stats, ["handovers", "handover_count"]) ?? currentSwingHandovers.length} icon={PenTool} />
        <StatTile label="Breakdowns" value={pickCount(stats, ["breakdowns", "breakdown_count"]) ?? currentSwingBreakdowns.length} icon={Wrench} />
        <StatTile label="Inspections" value={pickCount(stats, ["inspections", "inspection_count", "total_inspections"]) ?? completedInspections.length} icon={ClipboardList} />
        <StatTile label="Planning Requests" value={pickCount(stats, ["ppfs", "ppf_count"]) ?? completedPpfs.length} icon={PenTool} />
        <StatTile label="Custom Jobs" value={pickCount(stats, ["custom_jobs", "custom_submissions", "customjobs_count"]) ?? customJobs.length} icon={Layers} />
      </section>

      {/* Digger teeth % worn table — moved to the bottom of the page per
          Josh Feb 23 ("please just put percentage down the bottom") since
          it's a reference table, not primary shift-summary content. */}

      {/* Work Completed — from handovers this swing */}
      <section className="panel-heavy p-5 md:p-6" data-testid="ss-work-completed">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <MessageSquare className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Handovers · this shift</div>
            <div className="font-display font-black text-xl tracking-tight">WORK DONE</div>
          </div>
          <div className="text-[11px] font-mono-tight text-muted">
            {currentSwingHandovers.length} handover{currentSwingHandovers.length === 1 ? "" : "s"}
          </div>
        </div>
        {currentSwingHandovers.length === 0 ? (
          <div className="text-muted text-sm py-3">No handover work-completed entries this swing yet.</div>
        ) : (
          <div className="grid gap-3">
            {currentSwingHandovers.map((h) => (
              <div key={h.id || h._id} className="panel p-4" data-testid={`ss-ho-${h.id || h._id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="pill !text-[10px] !py-0.5 !px-2 font-mono-tight">{h.asset_id || "asset"}</span>
                  <span className="pill !text-[10px] !py-0.5 !px-2">{(h.shift || "shift").toUpperCase()}</span>
                  {h.tradesman && <span className="pill !text-[10px] !py-0.5 !px-2"><Users className="w-3 h-3" /> {h.tradesman}</span>}
                  {(h.created_at || h.date) && <span className="pill !text-[10px] !py-0.5 !px-2"><Clock className="w-3 h-3" /> {fmtDateTime(h.created_at || h.date)}</span>}
                </div>
                {h.work_completed && (
                  <div className="mb-2">
                    <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Work completed</div>
                    <div className="text-sm whitespace-pre-wrap">{h.work_completed}</div>
                  </div>
                )}
                {h.machine_status && (
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted mb-1">Machine status</div>
                    <div className="text-sm whitespace-pre-wrap">{h.machine_status}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Inspections & PPFs completed this shift */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ss-completions">
        <div className="panel-heavy p-5" data-testid="ss-inspections-done">
          <div className="flex items-center gap-2 mb-3">
            <FileCheck2 className="w-4 h-4" style={{ color: "var(--primary)" }} />
            <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">Inspections signed off</div>
            <div className="ml-auto text-[11px] font-mono-tight text-muted">{completedInspections.length}</div>
          </div>
          {completedInspections.length === 0 ? (
            <div className="text-muted text-sm py-3">Nothing signed off this shift yet.</div>
          ) : (
            <div className="grid gap-2">
              {completedInspections.slice(0, 12).map((i) => (
                <div key={i.id || i._id} className="panel p-3" data-testid={`ss-insp-${i.id || i._id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="pill !text-[10px] !py-0.5 !px-2 font-mono-tight">{i.asset_id || "asset"}</span>
                    {(i.approved_at || i.updated_at) && <span className="pill !text-[10px] !py-0.5 !px-2"><Clock className="w-3 h-3" /> {fmtDateTime(i.approved_at || i.updated_at)}</span>}
                    {i.supervisor_name && <span className="pill !text-[10px] !py-0.5 !px-2"><Users className="w-3 h-3" /> {i.supervisor_name}</span>}
                  </div>
                  <div className="text-sm font-display font-black truncate">
                    {i.template_name || i.title || "Inspection"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel-heavy p-5" data-testid="ss-ppfs-done">
          <div className="flex items-center gap-2 mb-3">
            <PenTool className="w-4 h-4" style={{ color: "var(--primary)" }} />
            <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">Planning Requests completed</div>
            <div className="ml-auto text-[11px] font-mono-tight text-muted">{completedPpfs.length}</div>
          </div>
          {completedPpfs.length === 0 ? (
            <div className="text-muted text-sm py-3">No Planning Requests completed this shift.</div>
          ) : (
            <div className="grid gap-2">
              {completedPpfs.slice(0, 12).map((p) => (
                <div key={p.id || p._id} className="panel p-3" data-testid={`ss-ppf-${p.id || p._id}`}>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="pill !text-[10px] !py-0.5 !px-2 font-mono-tight">{p.asset_id || "asset"}</span>
                    {(p.completed_at || p.updated_at) && <span className="pill !text-[10px] !py-0.5 !px-2"><Clock className="w-3 h-3" /> {fmtDateTime(p.completed_at || p.updated_at)}</span>}
                    {(p.completed_by_name || p.supervisor_name) && <span className="pill !text-[10px] !py-0.5 !px-2"><Users className="w-3 h-3" /> {p.completed_by_name || p.supervisor_name}</span>}
                  </div>
                  <div className="text-sm font-display font-black truncate">
                    {p.fault || p.title || "Planning Request"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Custom Jobs completed this shift — sourced from /shift-summary.custom_jobs */}
      <section className="panel-heavy p-5 md:p-6" data-testid="ss-custom-jobs">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Custom jobs signed off · this shift</div>
            <div className="font-display font-black text-xl tracking-tight">CUSTOM JOBS COMPLETED</div>
          </div>
          <div className="text-[11px] font-mono-tight text-muted">
            {customJobs.length} custom job{customJobs.length === 1 ? "" : "s"}
          </div>
        </div>
        {customJobs.length === 0 ? (
          <div className="text-muted text-sm py-3">No custom jobs signed off this shift yet.</div>
        ) : (
          <div className="grid gap-2 md:grid-cols-2">
            {customJobs.map((c) => (
              <div key={c.id || c._id} className="panel p-3" data-testid={`ss-cj-${c.id || c._id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span
                    className="pill !text-[9px] !py-0.5 !px-1.5 font-black uppercase tracking-widest shrink-0"
                    style={{ background: "rgba(74,222,128,0.14)", border: "1px solid rgba(74,222,128,0.35)", color: "#4ade80" }}
                  >
                    <ClipboardCheck className="w-3 h-3" /> DONE
                  </span>
                  <span className="pill !text-[10px] !py-0.5 !px-2 font-mono-tight">{c.asset_id || "asset"}</span>
                  {c.priority && String(c.priority).toLowerCase() !== "normal" && (
                    <span className="pill !text-[10px] !py-0.5 !px-2">{String(c.priority).toUpperCase()}</span>
                  )}
                  {(c.completed_at || c.closed_at || c.signed_at) && (
                    <span className="pill !text-[10px] !py-0.5 !px-2">
                      <Clock className="w-3 h-3" /> {fmtDateTime(c.completed_at || c.closed_at || c.signed_at)}
                    </span>
                  )}
                  {c.completed_by_name && (
                    <span className="pill !text-[10px] !py-0.5 !px-2">
                      <Users className="w-3 h-3" /> {c.completed_by_name}
                    </span>
                  )}
                </div>
                <div className="text-sm font-display font-black">
                  {c.text || c.title || c.description || "Custom job"}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Open custom jobs still outstanding — not signed off yet.
          Josh (Feb 26): "shift summer not showing all jobs down like custom
          jobs and that". Completed jobs live in the section above; still-open
          ones live here so nothing slips through the crew handover. */}
      {openJobs.length > 0 && (
        <section className="panel-heavy p-5 md:p-6" data-testid="ss-open-jobs">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                 style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
              <ClipboardList className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-muted">Custom jobs still open · carry-over</div>
              <div className="font-display font-black text-xl tracking-tight">OPEN CUSTOM JOBS</div>
            </div>
            <div className="text-[11px] font-mono-tight text-muted">
              {openJobs.length} outstanding
            </div>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {openJobs.map((c) => (
              <div key={c.id || c._id} className="panel p-3" data-testid={`ss-oj-${c.id || c._id}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span
                    className="pill !text-[9px] !py-0.5 !px-1.5 font-black uppercase tracking-widest shrink-0"
                    style={{ background: "rgba(245,130,32,0.14)", border: "1px solid rgba(245,130,32,0.35)", color: "var(--primary)" }}
                  >
                    OPEN
                  </span>
                  <span className="pill !text-[10px] !py-0.5 !px-2 font-mono-tight">{c.asset_id || "asset"}</span>
                  {c.priority && String(c.priority).toLowerCase() !== "normal" && (
                    <span className="pill !text-[10px] !py-0.5 !px-2">{String(c.priority).toUpperCase()}</span>
                  )}
                  {c.created_at && (
                    <span className="pill !text-[10px] !py-0.5 !px-2">
                      <Clock className="w-3 h-3" /> Raised {fmtDateTime(c.created_at)}
                    </span>
                  )}
                  {c.raised_by_name && (
                    <span className="pill !text-[10px] !py-0.5 !px-2">
                      <Users className="w-3 h-3" /> {c.raised_by_name}
                    </span>
                  )}
                </div>
                <div className="text-sm font-display font-black">
                  {c.text || c.title || c.description || "Custom job"}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Breakdowns + Recurring + Hazards */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="ss-issues-hazards">
        <div className="panel-heavy p-5" data-testid="ss-breakdowns">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="w-4 h-4" style={{ color: "var(--primary)" }} />
            <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">ASSET BREAKDOWNS</div>
          </div>
          {currentSwingBreakdowns.length === 0 ? (
            <div className="text-muted text-sm py-3">No breakdowns this shift.</div>
          ) : (
            <div className="grid gap-2">
              {currentSwingBreakdowns.slice(0, 10).map((b) => {
                const bStatus = String(b.status || "").toLowerCase();
                const approved = !!b.approved_at || bStatus === "approved";
                const awaiting = !approved && b.is_finalised;
                return (
                  <div key={b.id || b._id} className="panel p-3" data-testid={`ss-bd-${b.id || b._id}`}>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      {approved ? (
                        <span className="pill !text-[9px] !py-0.5 !px-1.5 font-black uppercase tracking-widest"
                              style={{ background: "rgba(74,222,128,0.14)", border: "1px solid rgba(74,222,128,0.35)", color: "#4ade80" }}>
                          <Check className="w-3 h-3" /> APPROVED
                        </span>
                      ) : awaiting ? (
                        <span className="pill !text-[9px] !py-0.5 !px-1.5 font-black uppercase tracking-widest"
                              style={{ background: "rgba(245,130,32,0.14)", border: "1px solid rgba(245,130,32,0.35)", color: "var(--primary)" }}>
                          AWAITING SIGN-OFF
                        </span>
                      ) : (
                        <span className="pill !text-[9px] !py-0.5 !px-1.5 font-black uppercase tracking-widest"
                              style={{ background: "rgba(148,163,184,0.15)", border: "1px solid rgba(148,163,184,0.3)", color: "#94a3b8" }}>
                          OPEN
                        </span>
                      )}
                      <span className="pill !text-[10px] !py-0.5 !px-2 font-mono-tight">{b.asset_id || "asset"}</span>
                      {b.priority && <span className="pill !text-[10px] !py-0.5 !px-2">{String(b.priority).toUpperCase()}</span>}
                      {b.breakdown_time && <span className="pill !text-[10px] !py-0.5 !px-2"><Clock className="w-3 h-3" /> {fmtDateTime(b.breakdown_time)}</span>}
                      {b.employee_name && <span className="pill !text-[10px] !py-0.5 !px-2"><Users className="w-3 h-3" /> {b.employee_name}</span>}
                      {approved && b.supervisor_name && (
                        <span className="pill !text-[10px] !py-0.5 !px-2">
                          <PenTool className="w-3 h-3" /> Signed: {b.supervisor_name}
                        </span>
                      )}
                    </div>
                    <div className="text-sm font-display font-black truncate">
                      {b.complaint || b.cause || b.title || "Breakdown"}
                    </div>
                    {b.corrective_action && <div className="text-[11px] text-muted mt-0.5 truncate">→ {b.corrective_action}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="panel-heavy p-5" data-testid="ss-recurring">
          <div className="flex items-center gap-2 mb-3">
            <Repeat className="w-4 h-4" style={{ color: "var(--primary)" }} />
            <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">Recurring issues</div>
          </div>
          {(summary?.recurring_issues || summary?.recurring || []).length === 0 ? (
            <div className="text-muted text-sm py-3">Nothing recurring flagged this swing.</div>
          ) : (
            <div className="grid gap-2">
              {(summary.recurring_issues || summary.recurring).slice(0, 10).map((r, i) => (
                <div key={i} className="panel p-3 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
                       style={{ background: "rgba(245,130,32,0.12)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.25)" }}>
                    <Repeat className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-black text-sm truncate">{r.title || r.label || r.description || r.summary || `Issue #${i + 1}`}</div>
                    <div className="text-[11px] font-mono-tight text-muted truncate">
                      {r.asset_id && <>{r.asset_id}</>}{(r.count || r.occurrences) && <> · {r.count || r.occurrences}× this week</>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel-heavy p-5" data-testid="ss-hazards">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-4 h-4" style={{ color: "#f87171" }} />
            <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">SITE HAZARDS THIS SHIFT</div>
          </div>
          {(summary?.hazards || summary?.active_hazards || []).length === 0 ? (
            <div className="text-muted text-sm py-3">No hazards this shift.</div>
          ) : (
            <div className="grid gap-2">
              {(summary.hazards || summary.active_hazards).slice(0, 10).map((h, i) => {
                const status = String(h.status || "pending").toLowerCase();
                const signedOff = status === "actioned" || status === "closed";
                return (
                  <div key={h.id || i} className="panel p-3 flex items-center gap-3" data-testid={`ss-hazard-${h.id || i}`}>
                    <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center"
                         style={{
                           background: signedOff ? "rgba(74,222,128,0.14)" : "#5a0f0f",
                           color: signedOff ? "#4ade80" : "#ff9b9b",
                           border: signedOff ? "1px solid rgba(74,222,128,0.35)" : "1px solid #a51919",
                         }}>
                      {signedOff ? <Check className="w-4 h-4" /> : <ShieldAlert className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                        <span
                          className="pill !text-[9px] !py-0.5 !px-1.5 font-black uppercase tracking-widest"
                          style={signedOff
                            ? { background: "rgba(74,222,128,0.14)", border: "1px solid rgba(74,222,128,0.35)", color: "#4ade80" }
                            : { background: "rgba(245,130,32,0.14)", border: "1px solid rgba(245,130,32,0.35)", color: "var(--primary)" }}
                        >
                          {signedOff ? "SIGNED OFF" : "PENDING"}
                        </span>
                        {h.risk_level && (
                          <span className="pill !text-[9px] !py-0.5 !px-1.5 uppercase">{h.risk_level}</span>
                        )}
                        {signedOff && h.actioned_by && (
                          <span className="text-[10px] font-mono-tight text-muted">
                            by {h.actioned_by}{h.actioned_at ? ` · ${fmtDateTime(h.actioned_at)}` : ""}
                          </span>
                        )}
                      </div>
                      <div className="font-display font-black text-sm truncate">{h.description || h.title || "Hazard"}</div>
                      <div className="text-[11px] font-mono-tight text-muted truncate">
                        {h.location && <>{h.location}</>}{h.crew && <> · {h.crew}</>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Digger teeth % worn table — reference data, kept at the bottom
          per Josh Feb 23 so the primary shift-log info (handovers,
          inspections, breakdowns, hazards) hits the eye first. Default
          sort is ALPHABETICAL by asset ID for easier scanning. */}
      <section className="panel-heavy p-5 md:p-6" data-testid="ss-get-wear">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center"
               style={{ background: "rgba(245,130,32,0.15)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.3)" }}>
            <Boxes className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-muted">Digger teeth % worn</div>
            <div className="font-display font-black text-xl tracking-tight">TEETH PERCENTAGE WORN</div>
          </div>
          <div className="text-[11px] font-mono-tight text-muted">
            {sorted.length} asset{sorted.length === 1 ? "" : "s"}
          </div>
        </div>

        {rows.length === 0 && !busy ? (
          <div className="panel-flat p-10 text-center text-muted">
            No teeth wear data reported. Log a bucket inspection to populate this table.
          </div>
        ) : (
          <div className="rounded-lg overflow-hidden" style={{ border: "1px solid var(--border)" }}>
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-[10px] uppercase tracking-widest font-black"
                 style={{ background: "var(--surface-2)", color: "var(--muted)", borderBottom: "1px solid var(--border)" }}>
              <HeadCell col="col-span-3" label="Asset" k="asset" state={{ sortKey, sortDir }} onClick={setSort} />
              <HeadCell col="col-span-2" label="Tooth %" k="worst_tooth" state={{ sortKey, sortDir }} onClick={setSort} />
              <HeadCell col="col-span-2" label="Lip Shroud %" k="worst_lip" state={{ sortKey, sortDir }} onClick={setSort} />
              <HeadCell col="col-span-2" label="Wing Shroud %" k="worst_wing" state={{ sortKey, sortDir }} onClick={setSort} />
              <HeadCell col="col-span-3" label="Last Inspected" k="last" state={{ sortKey, sortDir }} onClick={setSort} />
            </div>
            {sorted.map((r) => (
              <WearRow
                key={r.id || r.asset_id}
                row={r}
                busy={!!rowBusy[r.asset_id]}
                expanded={String(expanded) === String(r.asset_id)}
                onToggle={() => setExpanded(String(expanded) === String(r.asset_id) ? null : r.asset_id)}
                onAdjust={(patch) => adjustWear(r.asset_id, patch)}
                onMarkNew={(cat) => markNew(r.asset_id, cat)}
              />
            ))}
          </div>
        )}

        <div className="text-[10px] uppercase tracking-widest text-muted mt-3">
          Colour legend · <span style={{ color: BUCKET_TONE.new.color }}>NEW / &lt;25% worn</span> · <span style={{ color: BUCKET_TONE.s25.color }}>25% worn</span> · <span style={{ color: BUCKET_TONE.s50.color }}>50% worn</span> · <span style={{ color: BUCKET_TONE.s75.color }}>75% worn</span>
        </div>
      </section>

      {/* Optional: timeline if backend provided one */}
      {Array.isArray(summary?.timeline || summary?.events) && (summary.timeline || summary.events).length > 0 && (
        <section className="panel-heavy p-5 md:p-6" data-testid="ss-timeline">
          <div className="flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4" style={{ color: "var(--primary)" }} />
            <div className="text-[11px] uppercase tracking-[0.3em] font-black text-muted">Timeline</div>
          </div>
          <div className="grid gap-2">
            {(summary.timeline || summary.events).slice(0, 20).map((ev, i) => (
              <div key={i} className="panel p-3 flex items-center gap-3">
                <div className="w-2 h-2 rounded-full shrink-0" style={{ background: "var(--primary)" }} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-black text-sm truncate">{ev.title || ev.label || ev.kind || "Event"}</div>
                  <div className="text-[11px] text-muted font-mono-tight truncate">
                    {ev.who || ev.by} {ev.when && <>· {fmtDateTime(ev.when)}</>}
                    {ev.asset_id && <> · {ev.asset_id}</>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* ---------------- Wear row ---------------- */
function WearRow({ row, busy, expanded, onToggle, onAdjust, onMarkNew }) {
  const tooth = row.worst_tooth;
  const lip = row.worst_lip_shroud;
  const wing = row.worst_wing_shroud;
  const [tPct, setTPct] = useState(numOrDefault(tooth, ""));
  const [lPct, setLPct] = useState(numOrDefault(lip, ""));
  const [wPct, setWPct] = useState(numOrDefault(wing, ""));
  const [note, setNote] = useState("");

  useEffect(() => {
    setTPct(numOrDefault(tooth, ""));
    setLPct(numOrDefault(lip, ""));
    setWPct(numOrDefault(wing, ""));
  }, [tooth, lip, wing]);

  const applyPatch = async () => {
    const patch = {};
    if (tPct !== "" && Number(tPct) !== tooth) patch.tooth = Number(tPct);
    if (lPct !== "" && Number(lPct) !== lip) patch.lip_shroud = Number(lPct);
    if (wPct !== "" && Number(wPct) !== wing) patch.wing_shroud = Number(wPct);
    if (note) patch.note = note;
    if (Object.keys(patch).length === 0) return;
    await onAdjust(patch);
    setNote("");
  };

  return (
    <div style={{ borderTop: "1px solid var(--border)" }}>
      {/* Row */}
      <div
        className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center px-4 py-3"
        style={{ background: expanded ? "rgba(245,130,32,0.05)" : "var(--surface)" }}
        data-testid={`ss-wear-${row.asset_id || row.id}`}
      >
        <button type="button" onClick={onToggle} className="md:col-span-3 min-w-0 text-left hover:opacity-80">
          <div className="font-display font-black text-sm truncate flex items-center gap-2">
            <span className="font-mono-tight">{row.asset_id || "—"}</span>
            {row.asset_name && <span className="text-muted">· {row.asset_name}</span>}
          </div>
          <div className="text-[11px] text-muted mt-0.5 truncate">
            {row.asset_type || row.category || "digger"}{row.bucket_number ? ` · Bucket ${row.bucket_number}` : ""}
          </div>
        </button>
        <div className="md:col-span-2">
          <WearCellInline
            pct={tooth}
            onSet={(v) => v === 0 ? onMarkNew("tooth") : onAdjust({ tooth: v })}
            busy={busy}
            testidBase={`tooth-${row.asset_id}`}
          />
        </div>
        <div className="md:col-span-2">
          <WearCellInline
            pct={lip}
            onSet={(v) => v === 0 ? onMarkNew("lip_shroud") : onAdjust({ lip_shroud: v })}
            busy={busy}
            testidBase={`lip-${row.asset_id}`}
          />
        </div>
        <div className="md:col-span-2">
          <WearCellInline
            pct={wing}
            onSet={(v) => v === 0 ? onMarkNew("wing_shroud") : onAdjust({ wing_shroud: v })}
            busy={busy}
            testidBase={`wing-${row.asset_id}`}
          />
        </div>
        <div className="md:col-span-3 text-[11px] font-mono-tight text-muted flex items-center gap-2">
          {row.last_inspected ? (
            <div className="flex flex-wrap items-center gap-2 flex-1">
              <span className="pill !text-[10px] !py-0.5 !px-2"><Clock className="w-3 h-3" /> {fmtDateTime(row.last_inspected)}</span>
              {row.last_inspector && <span className="pill !text-[10px] !py-0.5 !px-2"><Users className="w-3 h-3" /> {row.last_inspector}</span>}
            </div>
          ) : (
            <span className="text-muted flex-1">Never</span>
          )}
          <button type="button" onClick={onToggle} className="text-muted hover:text-white shrink-0" title={expanded ? "Collapse" : "Adjust"} data-testid={`ss-wear-toggle-${row.asset_id}`}>
            {expanded ? <ChevronUp className="w-4 h-4" style={{ color: "var(--primary)" }} /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {/* Inline editor */}
      {expanded && (
        <div className="px-4 py-4"
             style={{ background: "rgba(255,255,255,0.02)", borderTop: "1px dashed var(--border)" }}
             data-testid={`ss-wear-editor-${row.asset_id}`}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
            <WearEditor label="Tooth %" pct={tPct} onChange={setTPct} onMarkNew={() => onMarkNew("tooth")} busy={busy} testidBase={`tooth-${row.asset_id}`} />
            <WearEditor label="Lip Shroud %" pct={lPct} onChange={setLPct} onMarkNew={() => onMarkNew("lip_shroud")} busy={busy} testidBase={`lip-${row.asset_id}`} />
            <WearEditor label="Wing Shroud %" pct={wPct} onChange={setWPct} onMarkNew={() => onMarkNew("wing_shroud")} busy={busy} testidBase={`wing-${row.asset_id}`} />
          </div>
          <div className="field">
            <label>Note (optional)</label>
            <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Cracked tooth #3, will replace next shutdown" data-testid={`ss-wear-note-${row.asset_id}`} />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button className="btn btn-ghost btn-sm" onClick={onToggle} disabled={busy}><XIcon className="w-3.5 h-3.5" /> Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={applyPatch} disabled={busy} data-testid={`ss-wear-save-${row.asset_id}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save wear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Inline wear cell — big % + colour-coded pill + compact NEW/25/50/75 picker.
 * Tapping any button immediately fires `onSet(pct)`; the mobile app picks up
 * the change via /get-wear + the latest inspection mirror.
 */
function WearCellInline({ pct, onSet, busy, testidBase }) {
  const curr = pct == null ? null : Number(pct);
  const b = pctBucket(curr ?? -1);
  const tone = BUCKET_TONE[b] || BUCKET_TONE.none;
  const PICKS = [
    { v: 0, label: "NEW" },
    { v: 25, label: "25" },
    { v: 50, label: "50" },
    { v: 75, label: "75" },
  ];
  return (
    <div className="flex flex-col gap-1.5" data-testid={`ss-wear-cell-${testidBase}`}>
      <div className="flex items-center gap-2">
        {curr == null ? (
          <span className="text-muted text-sm">—</span>
        ) : (
          <>
            <span className="font-display font-black tabular-nums text-lg" style={{ color: tone.color }}>
              {Math.round(curr)}%
            </span>
            <span
              className="pill !text-[9px] !py-0.5 !px-1.5"
              style={{ background: tone.bg, color: tone.color, border: `1px solid ${tone.border}` }}
            >
              {tone.label}
            </span>
          </>
        )}
        {busy && <Loader2 className="w-3 h-3 animate-spin text-muted ml-auto" />}
      </div>
      <div className="flex items-center gap-1" data-testid={`ss-wear-cell-${testidBase}-picks`}>
        {PICKS.map((p) => {
          const isCurrent = curr != null && Number(curr) === p.v;
          const pTone = BUCKET_TONE[pctBucket(p.v)];
          return (
            <button
              key={p.v}
              type="button"
              onClick={() => { if (!busy) onSet(p.v); }}
              disabled={busy}
              className="rounded-md py-1 px-1.5 text-[10px] font-black uppercase tracking-widest tabular-nums transition flex-1 min-w-0"
              style={{
                background: isCurrent ? pTone.bg : "rgba(255,255,255,0.03)",
                border: `1px solid ${isCurrent ? pTone.border : "var(--border)"}`,
                color: isCurrent ? pTone.color : "#a1a1aa",
                cursor: busy ? "not-allowed" : "pointer",
              }}
              data-testid={`ss-wear-cell-${testidBase}-${p.v}`}
              title={p.v === 0 ? "Reset this category to NEW" : `Set worst-of to ${p.v}%`}
            >
              {p.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Wear percentage semantics (mirrors the mobile app + inspection wear buttons):
//   0   → NEW (fresh teeth)
//   25  → 25% WORN     (still green)
//   50  → 50% WORN     (yellow — watch)
//   75  → 75% WORN     (orange — plan change-out)
//   100 → REPLACE      (red — change out immediately)
function pctBucket(v) {
  if (v == null) return "none";
  if (v >= 100) return "replace";
  if (v >= 75)  return "s75";
  if (v >= 50)  return "s50";
  if (v >= 25)  return "s25";
  return "new";  // 0-24% worn is still effectively fresh
}
const BUCKET_TONE = {
  new:     { color: "#4ade80", bg: "#0a2a12", border: "#1e6b32", label: "NEW" },
  s25:     { color: "#4ade80", bg: "#0a2a12", border: "#1e6b32", label: "25% WORN" },
  s50:     { color: "#facc15", bg: "#3a2f0d", border: "#c9a227", label: "50% WORN" },
  s75:     { color: "#ffb347", bg: "#4a2a06", border: "#f58220", label: "75% WORN" },
  replace: { color: "#ff9b9b", bg: "#5a0f0f", border: "#a51919", label: "REPLACE" },
  none:    { color: "#71717a", bg: "#141414", border: "#2a2a2a", label: "—" },
};

function WearEditor({ label, pct, onChange, onMarkNew, busy, testidBase }) {
  return (
    <div className="field">
      <label>{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="range" min={0} max={100} step={1}
          value={pct === "" ? 0 : Number(pct)}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1"
          disabled={busy}
          data-testid={`ss-wear-${testidBase}-range`}
        />
        <input
          type="number" min={0} max={100}
          value={pct}
          onChange={(e) => onChange(e.target.value)}
          className="input !w-20 !text-center font-mono-tight"
          disabled={busy}
          data-testid={`ss-wear-${testidBase}-input`}
        />
        <button
          type="button"
          className="btn btn-ghost btn-sm !px-2 !py-1"
          onClick={onMarkNew}
          disabled={busy}
          title="Mark this component as brand new (100%)"
          data-testid={`ss-wear-${testidBase}-marknew`}
        >
          <Sparkles className="w-3.5 h-3.5" /> NEW
        </button>
      </div>
    </div>
  );
}
function WearCell({ pct, tone }) {
  if (pct === null || pct === undefined) return <span className="text-muted text-sm">—</span>;
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 rounded-full overflow-hidden shrink-0"
           style={{ background: "rgba(255,255,255,0.06)", border: "1px solid var(--border)" }}>
        <div className="h-full transition-all" style={{ width: `${Math.max(0, Math.min(100, pct))}%`, background: tone.color }} />
      </div>
      <span className="font-display font-black tabular-nums text-base" style={{ color: tone.color }}>{Math.round(pct)}%</span>
    </div>
  );
}
function StatTile({ label, value, icon: Icon }) {
  if (value == null) return null;
  return (
    <div className="panel p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center"
           style={{ background: "rgba(245,130,32,0.12)", color: "var(--primary)", border: "1px solid rgba(245,130,32,0.25)" }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-muted">{label}</div>
        <div className="font-display font-black text-3xl tabular-nums leading-none">{value}</div>
      </div>
    </div>
  );
}
function HeadCell({ col, label, k, state, onClick }) {
  const active = state.sortKey === k;
  return (
    <button type="button" className={`${col} text-left flex items-center gap-1 hover:text-white`} onClick={() => onClick(k)}>
      {label}
      {active ? (state.sortDir === "asc" ? <ChevronUp className="w-3 h-3" style={{ color: "var(--primary)" }} /> : <ChevronDown className="w-3 h-3" style={{ color: "var(--primary)" }} />) : <span className="w-3 h-3" />}
    </button>
  );
}

/* ---------------- helpers ---------------- */
function normalise(r) {
  return {
    id: r.id || r._id || r.asset_id,
    asset_id: r.asset_id || r.id,
    asset_name: r.asset_name || r.name,
    asset_type: r.asset_type || r.category,
    bucket_number: r.bucket_number,
    worst_tooth: firstNumber(r, ["worst_tooth", "worst_tooth_pct", "tooth_pct", "tooth", "min_tooth", "worst_tooth_percent"]),
    worst_lip_shroud: firstNumber(r, ["worst_lip_shroud", "worst_lip", "lip_shroud", "lip_shroud_pct", "min_lip_shroud", "worst_lip_shroud_percent"]),
    worst_wing_shroud: firstNumber(r, ["worst_wing_shroud", "wing_shroud", "wing_shroud_pct"]),
    last_inspected: r.last_inspected || r.last_inspected_at || r.updated_at || r.checked_at,
    last_inspector: r.last_inspector || r.inspector_name || r.last_updated_by || r.updated_by,
  };
}
function firstNumber(o, keys) {
  for (const k of keys) {
    const raw = o?.[k];
    if (raw === undefined || raw === null || raw === "") continue;
    // /get-wear returns objects like {label, status, percent} for worst_* fields
    let v = raw;
    if (typeof v === "object") {
      if (v.percent != null) v = v.percent;
      else if (v.status != null) v = String(v.status).toLowerCase() === "new" ? 0 : v.status;
      else continue;
    }
    // "new" token means 0% worn (fresh teeth)
    if (typeof v === "string") {
      if (v.toLowerCase() === "new") return 0;
    }
    const n = Number(v);
    if (!isNaN(n)) return n <= 1 ? n * 100 : n;
  }
  return null;
}
function pickCount(stats, keys) {
  for (const k of keys) {
    if (stats?.[k] != null) return Number(stats[k]);
  }
  return null;
}
function numOrNull(v) { const n = Number(v); return isNaN(n) ? null : n; }
function numOrDefault(v, d) { const n = Number(v); return v == null || v === "" || isNaN(n) ? d : Math.round(n); }

const WEAR_TONE = {
  new:     { color: "#4ade80" },
  good:    { color: "#8bb4ff" },
  watch:   { color: "#ffb347" },
  replace: { color: "#f87171" },
  none:    { color: "#71717a" },
};
function wearTone(v) {
  if (v == null) return WEAR_TONE.none;
  if (v >= 80) return WEAR_TONE.new;
  if (v >= 60) return WEAR_TONE.good;
  if (v >= 40) return WEAR_TONE.watch;
  return WEAR_TONE.replace;
}
function currentShift() {
  // Legacy heuristic — replaced live by useSiteShift() in the component.
  const h = new Date().getHours();
  return (h >= 6 && h < 18) ? "day" : "night";
}

/** ISO-8601 week number: Thursday-based, Jan 4 is always in week 1. */
function weekOfYear(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}
function fmtDate(v) { try { return new Date(v).toLocaleDateString([], { weekday: "long", day: "numeric", month: "long" }); } catch { return String(v || ""); } }
function fmtDateTime(v) { try { return new Date(v).toLocaleString([], { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return String(v || ""); } }

// Keep Download import used (planned PDF export coming soon)
